import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";

export interface RouteWaypoint {
  id: string;
  route_id: string;
  name: string;
  description?: string;
  geom: any; // GeoJSON Point
  waypoint_order: number;
  waypoint_type: 'poi' | 'hazard' | 'rest_stop' | 'scenic' | 'technical' | 'camping' | 'water' | 'fuel';
  photo_urls: string[];
  created_at: string;
  updated_at: string;
}

export const useRouteWaypoints = (routeId: string | undefined) => {
  const [waypoints, setWaypoints] = useState<RouteWaypoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRouteWaypoints = async () => {
      if (!routeId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const { data, error: fetchError } = await supabase
          .from('route_waypoints')
          .select('*')
          .eq('route_id', routeId)
          .order('waypoint_order');

        if (fetchError) {
          console.error('Error fetching route waypoints:', fetchError);
          setError('Failed to load route waypoints');
          return;
        }

        // Transform data to match our interface
        const transformedData = (data || []).map(item => ({
          ...item,
          photo_urls: Array.isArray(item.photo_urls) ? item.photo_urls as string[] : [],
          description: item.description || undefined
        }));
        setWaypoints(transformedData);
      } catch (err) {
        console.error('Error fetching route waypoints:', err);
        setError('Failed to load route waypoints');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRouteWaypoints();
  }, [routeId]);

  const addWaypoint = async (waypoint: Omit<RouteWaypoint, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('route_waypoints')
        .insert([waypoint])
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const transformedData = {
          ...data,
          photo_urls: Array.isArray(data.photo_urls) ? data.photo_urls as string[] : [],
          description: data.description || undefined
        };
        setWaypoints(prev => [...prev, transformedData].sort((a, b) => a.waypoint_order - b.waypoint_order));
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error adding route waypoint:', error);
      return { data: null, error };
    }
  };

  const updateWaypoint = async (id: string, updates: Partial<RouteWaypoint>) => {
    try {
      const { data, error } = await supabase
        .from('route_waypoints')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const transformedData = {
          ...data,
          photo_urls: Array.isArray(data.photo_urls) ? data.photo_urls as string[] : [],
          description: data.description || undefined
        };
        setWaypoints(prev => prev.map(wp => wp.id === id ? transformedData : wp));
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating route waypoint:', error);
      return { data: null, error };
    }
  };

  const deleteWaypoint = async (id: string) => {
    try {
      const { error } = await supabase
        .from('route_waypoints')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setWaypoints(prev => prev.filter(wp => wp.id !== id));
      return { error: null };
    } catch (error) {
      console.error('Error deleting route waypoint:', error);
      return { error };
    }
  };

  return { 
    waypoints, 
    isLoading, 
    error, 
    addWaypoint, 
    updateWaypoint, 
    deleteWaypoint 
  };
};